// SZÁMLÁIM — a kiállított / kiegyenlített / érvénytelenített számlák listája (fül + kereső), a lejárt-
// figyelmeztetéssel, a közös-számla jelöléssel és egy tétel-panellel (`invoiceDetail`).
//
// ELTÉRÉS A TERVTŐL: a LEJÁRT összeg DEVIZÁNKÉNT bontva jelenik meg (a terv egyetlen forintos számot mutat).
// Egy fizetési felszólítás helyén az átszámított összeg félrevezető lenne — a partnernek EUR-os számlája is
// lehet. A fiók-szűkítés MINDEN kérésben ott van (a sidebar váltójából).
import type { PortalInvoiceRow } from '@atlas/contracts';
import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InvoiceStatusBadge } from '@/components/invoices/invoice-status-badge';
import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalHiddenSharedNotice } from '@/components/portal/portal-hidden-shared-notice';
import { PortalInvoiceDetailSheet } from '@/components/portal/portal-invoice-detail-sheet';
import { PortalInvoiceScope } from '@/components/portal/portal-invoice-scope';
import { PortalSharedBadge } from '@/components/portal/portal-shared-badge';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/empty-state';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { MoneyBlock, formatMoney } from '@/components/shared/money-block';
import { Button } from '@/components/ui/button';
import { TabStrip } from '@/components/ui/tab-strip';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { formatDay } from '@/features/portal/billing-format';
import { usePortal } from '@/features/portal/portal-context';
import { useInvoicePdf } from '@/features/portal/use-invoice-pdf';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { daysUntilDay } from '@/lib/format-datetime';
import { cn } from '@/lib/utils';

const DEFAULTS = { tab: 'issued' as 'issued' | 'paid' | 'storno', q: '' };

/**
 * A HATÁRIDŐ emberi olvasata: „N napja lejárt" (danger) vagy „N nap múlva esedékes". Csak `overdue` és
 * `issued` státuszon értelmes — a többinél nincs mit mondani, és `null`-t adunk (nem üres sort).
 */
function DueHint({
  status,
  dueDate,
}: {
  status: string;
  dueDate: string | null;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (dueDate === null || (status !== 'overdue' && status !== 'issued')) return null;
  const days = daysUntilDay(dueDate);
  if (days === null) return null;
  if (days < 0) {
    return (
      <span className="text-danger-600 text-[11.5px] font-medium">
        {t('portal.invoices.overdueSince', { count: Math.abs(days) })}
      </span>
    );
  }
  return (
    <Text variant="secondary" as="span">
      {t('portal.invoices.dueIn', { count: days })}
    </Text>
  );
}

export function PortalBillingInvoicesView(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { accountId } = usePortal();
  const { filters, setFilters } = useListFilters(DEFAULTS);
  const pdf = useInvoicePdf();
  const [detailRow, setDetailRow] = useState<PortalInvoiceRow | null>(null);
  const tab: 'issued' | 'paid' | 'storno' =
    filters.tab === 'paid' ? 'paid' : filters.tab === 'storno' ? 'storno' : 'issued';

  const q = useQuery({
    // A FIÓK-SCOPE a kulcs része: váltásnál új kérés (nem kliens-oldali utószűrés).
    queryKey: portalKeys.invoiceList({ ...filters, tab, account: accountId }),
    queryFn: () =>
      api.portal.invoices.query({
        query: {
          tab,
          ...(filters.q ? { q: filters.q } : {}),
          // A SZŰKÍTÉS a sidebar fiók-váltójából (egy vezérlő, egy igazság) — `null` → „Minden fiók".
          ...(accountId !== null ? { accountId } : {}),
          lang: i18n.language === 'en' ? 'en' : 'hu',
        },
      }),
  });
  const body = q.data?.status === 200 ? q.data.body : undefined;
  const isForbidden = q.data?.status === 403;
  const isError = !q.isLoading && q.data !== undefined && q.data.status !== 200 && !isForbidden;

  const columns: DataTableColumn<PortalInvoiceRow>[] = [
    {
      id: 'number',
      header: t('portal.invoices.columns.number'),
      cardLabel: t('portal.invoices.columns.number'),
      card: 'title',
      width: '160px',
      multiline: true,
      cell: (r) => (
        <div className="flex min-w-0 flex-col items-start gap-1.5">
          <span className="font-mono text-xs font-medium tabular-nums">
            {r.status === 'draft' ? t('portal.billing.invoice.draft') : r.invoiceNumber}
          </span>
          {/* A KÖZÖS `InvoiceStatusBadge` (a belső számla-lista jelvénye): szín ÉS vezető IKON egy helyről —
              kiállított → repülő, fizetve → pipa, lejárt → figyelmeztető háromszög, sztornó → vissza-nyíl.
              Korábban itt egy saját státusz→szín térkép állt IKON NÉLKÜL, tehát ugyanaz az állapot a két
              felületen máshogy nézett ki (user-lelet), és a fül-sáv ikonjaival sem egyezett. */}
          <InvoiceStatusBadge
            status={r.status}
            label={
              r.status === 'draft'
                ? t('portal.billing.invoice.draft')
                : t(`portal.invoices.status.${r.status}`, { defaultValue: r.status })
            }
          />
        </div>
      ),
    },
    {
      id: 'buyer',
      header: t('portal.invoices.columns.buyer'),
      cardLabel: t('portal.invoices.columns.buyer'),
      card: 'subtitle',
      width: 'minmax(190px,1fr)',
      multiline: true,
      cell: (r) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <Text variant="itemTitle" as="span" className="truncate">
            {r.billingName ?? '—'}
          </Text>
          <Text variant="secondary" as="span" className="truncate">
            {r.billingAddress ?? ''}
          </Text>
          <PortalInvoiceScope accountName={r.accountName} />
          <PortalSharedBadge shared={r.shared} className="mt-1" />
        </div>
      ),
    },
    {
      id: 'amount',
      header: t('portal.invoices.columns.amount'),
      cardLabel: t('portal.invoices.columns.amount'),
      width: '158px',
      multiline: true,
      cell: (r) => <MoneyBlock gross={r.gross} net={r.net} vat={r.vat} currency={r.currency} />,
    },
    {
      id: 'dates',
      header: t('portal.invoices.columns.dates'),
      cardLabel: t('portal.invoices.columns.dates'),
      width: '176px',
      multiline: true,
      cell: (r) => (
        <div className="flex min-w-0 flex-col items-start gap-0.5 text-[11.5px]">
          <Text variant="secondary" as="span">
            {t('portal.invoices.issued')}{' '}
            <span className="font-mono tabular-nums">{formatDay(r.issueDate, i18n.language)}</span>
          </Text>
          <Text variant="secondary" as="span">
            {t('portal.invoices.due')}{' '}
            <span
              className={cn('font-mono tabular-nums', r.status === 'overdue' && 'text-danger-600')}
            >
              {formatDay(r.dueDate, i18n.language)}
            </span>
          </Text>
          {/* HARMADIK SOR (a terv szerint): ha fizetve → a fizetés napja; ha lejárt → MENNYIRE lejárt;
              ha kiállított → mikor esedékes. A partner számára ez a leghasznosabb egyetlen adat a lapon:
              a puszta határidő-dátumból fejben kell kivonni a mait. */}
          {r.paymentDate !== null ? (
            <Text variant="secondary" as="span">
              {t('portal.invoices.paid')}{' '}
              <span className="font-mono tabular-nums">
                {formatDay(r.paymentDate, i18n.language)}
              </span>
            </Text>
          ) : (
            <DueHint status={r.status} dueDate={r.dueDate} />
          )}
        </div>
      ),
    },
    {
      id: 'details',
      header: t('portal.billing.invoice.details'),
      cardLabel: t('portal.billing.invoice.details'),
      width: '96px',
      cell: (r) => (
        <Button
          variant="outline"
          size="sm"
          aria-label={t('portal.billing.invoice.detailsAria', { number: r.invoiceNumber })}
          onClick={() => setDetailRow(r)}
        >
          {t('portal.billing.invoice.details')}
        </Button>
      ),
    },
    {
      id: 'pdf',
      header: t('portal.invoices.columns.pdf'),
      cardLabel: t('portal.invoices.columns.pdf'),
      width: '96px',
      cell: (r) => (
        <Button
          variant="outline"
          size="sm"
          disabled={!r.canDownloadPdf || pdf.downloading === r.id}
          aria-label={t('portal.invoices.downloadAria', { number: r.invoiceNumber })}
          onClick={() => void pdf.download(r.id)}
        >
          <ArrowDownTrayIcon />
          PDF
        </Button>
      ),
    },
  ];

  if (isForbidden) {
    return (
      <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
    );
  }

  return (
    <div className="space-y-8">
      {/* LEJÁRT-FIGYELMEZTETÉS — devizánként bontva (lásd a fájl-fejléc érvelését). */}
      {body !== undefined && body.overdue.count > 0 && (
        <div className="bg-danger-soft flex items-center gap-3 rounded-xl p-3.5 text-[12.5px] leading-relaxed">
          <ExclamationTriangleIcon className="text-danger-600 size-[18px] shrink-0" />
          <span className="flex-1">
            <strong>{t('portal.invoices.overdueTitle', { count: body.overdue.count })}</strong>{' '}
            {t('portal.invoices.overdueTotal', {
              amounts: body.overdue.totals
                .map((x) => formatMoney(x.gross, x.currency, i18n.language))
                .join(' + '),
            })}
          </span>
          {/* A terv „Megnézem" gombja: a figyelmeztetésből EGY kattintás a lejárt tételekhez (a „kiállított"
              fül tartalmazza őket) — a partnernek ne kelljen kitalálnia, hol keresse. */}
          {tab !== 'issued' && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setFilters({ tab: 'issued' })}
            >
              {t('portal.invoices.showOverdue')}
            </Button>
          )}
        </div>
      )}

      {/* A FÜL-SÁV a saját sorában, ALATTA a KÖZÖS `ListToolbar` — pontosan a portál JEGY-LISTÁJÁNAK (és az
          admin-listáknak) a geometriája. NINCS `space-y`: a szakaszok közét a `ListToolbar` SAJÁT `py-2.5`-e
          adja (10px). Korábban itt egy kézzel összeállított sáv állt (a fülek MELLETT a kereső és a
          darabszám): a magasságok, a térközök és a kereső viselkedése így garantáltan elcsúszott a többi
          lista-oldaltól. */}
      <PortalHiddenSharedNotice
        kind="invoices"
        count={body?.hiddenSharedCount ?? 0}
        className="-mt-3"
      />

      <section>
        <div className="flex flex-wrap items-center gap-2">
          <TabStrip
            value={tab}
            onChange={(v) =>
              setFilters({ tab: v === 'paid' ? 'paid' : v === 'storno' ? 'storno' : 'issued' })
            }
            // AZ IKONOK a SZÁMLA-STÁTUSZ-JELVÉNY ikonjai (`invoice-status-badge.tsx`): kiállított →
            // papírrepülő, fizetett → pipa, sztornó → vissza-nyíl. Egy állapot = egy jel az egész
            // rendszerben, tehát a fül és a soron látható jelvény ugyanazt mondja.
            items={[
              {
                value: 'issued',
                label: t('portal.invoices.tabs.issued'),
                icon: <PaperAirplaneIcon />,
                ...(body ? { count: body.counts.issued } : {}),
              },
              {
                value: 'paid',
                label: t('portal.invoices.tabs.paid'),
                icon: <CheckCircleIcon />,
                ...(body ? { count: body.counts.paid } : {}),
              },
              {
                value: 'storno',
                label: t('portal.invoices.tabs.storno'),
                icon: <ArrowUturnLeftIcon />,
                ...(body ? { count: body.counts.storno } : {}),
              },
            ]}
            aria-label={t('portal.billing.views.invoices')}
          />
        </div>

        <ListToolbar
          // `px-0`: a sáv alap-paddingja a BELSŐ lista-oldalakhoz van hangolva (ott a szélig futó sticky-sáv
          // hozza vele a lap-margót). A portál-keret tartalom-sávjának MÁR van margója — ugyanaz a döntés,
          // mint a portál jegy-listáján.
          className="px-0"
          search={{
            value: filters.q,
            onChange: (v: string) => setFilters({ q: v }),
            placeholder: t('common.searchPlaceholder'),
            ariaLabel: t('portal.invoices.searchLabel'),
          }}
          // SZŰRŐ-POPOVER NINCS: a durva bontást a fül-sáv adja (kiállított · kiegyenlített ·
          // érvénytelenített), a fiók-szűkítést pedig a keret fiók-váltója — egy vezérlő, egy igazság. A
          // `ListToolbar` a szűrő-gombot magától elhagyja, ha nincs szűrő-elem.
          meta={
            body ? (
              <span aria-live="polite">
                {t('portal.invoices.count', { count: body.invoices.length })}
              </span>
            ) : undefined
          }
        />

        {/* NINCS `Card` burkoló: a `DataTable` MAGA a kártya (`card-glass-lg`), mobilon pedig KÁRTYÁKAT
            renderel a sorokból — a burkoló miatt egy NAGY kártyán belül ültek a kis kártyák (ugyanaz a
            user-lelet, mint a portál jegy-listáján; az admin-listák sem wrappelik). */}
        {isError ? (
          <PortalErrorState onRetry={() => void q.refetch()} />
        ) : (
          <DataTable<PortalInvoiceRow>
            columns={columns}
            rows={body?.invoices ?? []}
            getRowId={(r) => r.id}
            isLoading={q.isLoading}
            emptyState={
              <EmptyState
                title={t('portal.invoices.empty.title')}
                description={t(`portal.invoices.empty.${tab}`)}
              />
            }
          />
        )}
      </section>

      <PortalInvoiceDetailSheet invoice={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  );
}
